import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

/**
 * Browser dictation via the Web Speech API (Chrome/Edge/Safari; absent in
 * Firefox — check `supported`). Audio is processed by the browser vendor's
 * speech service. Final phrases are delivered through `onFinalText`;
 * recognition ends on tap or after the browser's silence timeout.
 */

interface SpeechAlternativeLike {
  transcript: string;
}

interface SpeechResultLike {
  isFinal: boolean;
  0: SpeechAlternativeLike;
}

interface SpeechRecognitionEventLike {
  resultIndex: number;
  results: ArrayLike<SpeechResultLike>;
}

interface SpeechRecognitionLike {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  onresult: ((event: SpeechRecognitionEventLike) => void) | null;
  onend: (() => void) | null;
  onerror: ((event: { error: string }) => void) | null;
  start(): void;
  stop(): void;
}

type SpeechRecognitionCtor = new () => SpeechRecognitionLike;

const getRecognitionCtor = (): SpeechRecognitionCtor | null => {
  const w = window as unknown as {
    SpeechRecognition?: SpeechRecognitionCtor;
    webkitSpeechRecognition?: SpeechRecognitionCtor;
  };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
};

const REGION_BY_LANGUAGE: Record<string, string> = {
  en: "en-US",
  de: "de-DE",
  ja: "ja-JP",
  uk: "uk-UA",
  pl: "pl-PL",
  ar: "ar-SA",
};

const toBcp47 = (language: string): string =>
  language.includes("-") ? language : (REGION_BY_LANGUAGE[language] ?? "en-US");

export function useSpeechDictation(onFinalText: (text: string) => void) {
  const { i18n } = useTranslation();
  const [listening, setListening] = useState(false);
  const [denied, setDenied] = useState(false);
  const recognitionRef = useRef<SpeechRecognitionLike | null>(null);
  const onTextRef = useRef(onFinalText);
  onTextRef.current = onFinalText;

  const stop = useCallback(() => {
    recognitionRef.current?.stop();
  }, []);

  const start = useCallback(() => {
    const Recognition = getRecognitionCtor();
    if (!Recognition || recognitionRef.current) return;
    const recognition = new Recognition();
    recognition.lang = toBcp47(i18n.language);
    recognition.continuous = true;
    recognition.interimResults = false;
    recognition.onresult = (event) => {
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        const result = event.results[i];
        if (result.isFinal) {
          const transcript = result[0].transcript.trim();
          if (transcript) onTextRef.current(transcript);
        }
      }
    };
    recognition.onend = () => {
      recognitionRef.current = null;
      setListening(false);
    };
    recognition.onerror = (event) => {
      if (event.error === "not-allowed" || event.error === "service-not-allowed") {
        setDenied(true);
      }
    };
    recognitionRef.current = recognition;
    setDenied(false);
    setListening(true);
    recognition.start();
  }, [i18n.language]);

  useEffect(() => () => recognitionRef.current?.stop(), []);

  return { supported: getRecognitionCtor() !== null, listening, denied, start, stop };
}
